/**
 * i18n translation dictionaries.
 *
 * Each language is an object keyed by i18n key (e.g. `nav.portfolio`).
 * Lookup falls back English → key, so non-English locales can ship partial
 * coverage and the engine fills the gaps from English. Adding a new
 * language is just adding a new top-level key.
 *
 * Adding new keys: append to the `en` block AND any localized blocks you
 * have translations for. The fallback chain handles the rest.
 *
 * Mirrors the prototype's `js/i18n.js` `TRANSLATIONS` object structure —
 * but ported as a TypeScript-typed const + curated seed of the highest-
 * frequency UI strings. The prototype carries ~466 keys × 15 languages
 * which lands in subsequent passes.
 */

export type SupportedLang =
  | 'en' | 'es' | 'fr' | 'de' | 'pt'
  | 'ja' | 'zh' | 'ko' | 'it' | 'nl'
  | 'ar' | 'hi' | 'th' | 'pl' | 'tr';

export const SUPPORTED_LANGS: readonly SupportedLang[] = [
  'en', 'es', 'fr', 'de', 'pt',
  'ja', 'zh', 'ko', 'it', 'nl',
  'ar', 'hi', 'th', 'pl', 'tr',
] as const;

export const LANG_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  ja: '日本語',
  zh: '中文',
  ko: '한국어',
  it: 'Italiano',
  nl: 'Nederlands',
  ar: 'العربية',
  hi: 'हिन्दी',
  th: 'ไทย',
  pl: 'Polski',
  tr: 'Türkçe',
};

/** Languages that render right-to-left. */
export const RTL_LANGS: ReadonlySet<SupportedLang> = new Set(['ar']);

type TranslationDict = Record<string, string>;

const en: TranslationDict = {
  // Header / branding
  'header.appname': 'Clara intelligence',
  'header.clara': 'Clara',
  'header.intelligence': 'intelligence',

  // Navigation
  'nav.portfolio': 'Portfolio',
  'nav.focus': 'New Chat',
  'nav.workspace': 'Workspace',
  'nav.home': 'Home',
  'nav.back': 'Back',
  'nav.agents': 'Agents',
  'nav.clients': 'Clients',
  'nav.workspaces': 'Workspaces',
  'nav.tabs': 'Tabs',
  'nav.search.placeholder': 'Search',

  // Dock
  'dock.home': 'Home',
  'dock.clients': 'Clients',
  'dock.workspaces': 'Workspaces',
  'dock.agents': 'Agents',
  'dock.pulse': 'View Pulse',

  // Tabs
  'tab.chat': 'Chat',
  'tab.dashboard': 'Dashboard',
  'tab.newChat': 'New Chat',
  'tab.newWorkspace': 'New Workspace',
  'tab.newAgent': 'New Agent',
  'tab.closeAllTabs': 'Close all tabs',
  'tab.requiredActions': 'Required Action',
  'tab.todaysTasks': "Today's Tasks",
  'tab.activeChats': 'Active Chats',

  // Chat
  'chat.placeholder': 'Ask Clara anything...',
  'chat.askAnything': 'Ask anything...',
  'chat.askFollowUp': 'Ask follow-up questions...',
  'chat.thinking': 'Thinking...',
  'chat.send': 'Send',
  'chat.reply': 'Reply...',

  // Focus / Morning Update
  'focus.greeting.morning': 'Good morning',
  'focus.greeting.afternoon': 'Good afternoon',
  'focus.greeting.evening': 'Good evening',
  'focus.status.checking': 'Checking for updates ...',
  'focus.status.found': 'I found something that requires your attention.',
  'focus.status.nothing': 'No updates. What are you thinking?',
  'focus.quickActions': 'Quick Actions',

  // Mode toggle
  'mode.work': 'Work',
  'mode.web': 'Web',

  // Engagement / Workspace landing
  'eng.home': 'Home',
  'eng.calendar': 'Calendar',
  'eng.tasks': 'Tasks',
  'eng.context': 'Context',
  'eng.reasoning': 'Reasoning',
  'eng.activityLog': 'User Edit Log',
  'eng.milestones': 'Milestones',
  'eng.search': 'Search',
  'eng.filter': 'Filter',

  // Panels
  'panel.context': 'Context',
  'panel.workspaceContext': 'Active Context',
  'panel.activeChatContext': 'Active Context',
  'panel.dashboardContext': 'Dashboard Context',
  'panel.history': 'History',
  'panel.viewPulse': 'Pulse insights',
  'panel.notifications': 'Notifications',

  // AI panel
  'ai.disclaimer': 'AI-generated content may be incorrect',
  'ai.claraAi': 'Clara AI',
  'ai.projectChat': 'Project Chat',
  'ai.welcome.title': "Hi Sarah, I'm Clara.",
  'ai.welcome.desc': "I can help you understand your engagements, prep for meetings, or just answer a question.",
  'ai.welcome.tipsLabel': 'TRY ASKING',

  // Profile
  'profile.notifications': 'Notifications',
  'profile.darkMode': 'Dark mode',
  'profile.lightMode': 'Light mode',
  'profile.language': 'Language',
  'profile.feedback': 'Give feedback',
  'profile.title': 'Profile',
  'profile.loading': 'Loading...',
  'profile.disclaimer': 'Acceptable use disclaimer',
  'disclaimer.accept': 'I Agree',

  // Settings / Header expanded keys
  'settings.title': 'Settings',
  'settings.memory': 'Memory settings',
  'settings.referenceSavedMemories': 'Reference saved memories',
  'settings.referenceChatHistory': 'Reference chat history',
  'settings.manageSavedMemories': 'Manage saved memories',
  'header.language': 'Language',
  'header.darkMode': 'Dark mode',
  'header.lightMode': 'Light mode',
  'header.searchLanguagePlaceholder': 'Search language...',
  'header.noLanguageResults': 'No matching languages found.',
  'header.welcomeTutorial': 'Welcome Tutorial',
  'header.systemCard': 'System Card',
  'header.guides': 'Guides',
  'header.brandPrimary': 'Clara',
  'header.brandSecondary': 'intelligence',
  'header.viewWorkspace': 'View workspace',
  'header.alertA.text': 'Maria needs sign-off before Thursday for Pink Garments FY29.',
  'header.alertS.text': 'AlphaGo FY28 onsite dates shifted - timeline needs your review.',

  // Language labels
  'languages.en': 'English',
  'languages.es': 'Spanish',
  'languages.fr': 'French',
  'languages.de': 'German',
  'languages.pt': 'Portuguese',
  'languages.ja': 'Japanese',
  'languages.zh': 'Chinese',
  'languages.ko': 'Korean',
  'languages.it': 'Italian',
  'languages.nl': 'Dutch',
  'languages.ar': 'Arabic',
  'languages.hi': 'Hindi',
  'languages.th': 'Thai',
  'languages.pl': 'Polish',
  'languages.tr': 'Turkish',

  // Feedback modal
  'feedback.header': 'Feedback Submission',
  'feedback.description.1': 'Please provide your feedback / recommendations for improvement',
  'feedback.description.2.1': 'Please be sure to review the',
  'feedback.description.2.2': 'Clara AI Product Page',
  'feedback.description.2.3': 'for all resources and FAQs',
  'feedback.categoryLabel': 'Feedback pertains to:',
  'feedback.customFeedbackPlaceholder': 'Type custom feedback category',
  'feedback.ratingLabel': 'Overall Rating',
  'feedback.detailsLabel': 'Feedback details',
  'feedback.footnote0': 'Personal Identifiable Information (PII),',
  'feedback.footnote1': '*Client Confidential Information (CCI)',
  'feedback.footnote2': 'Protected Health Information (PHI) and Firm Confidential Information (FCI) should NOT be included within your feedback.',
  'feedback.option.general': 'General',
  'feedback.option.accuracy': 'Accuracy of responses',
  'feedback.option.speed': 'Speed / performance',
  'feedback.option.usability': 'Usability / experience',
  'feedback.option.feature': 'Feature request',
  'feedback.option.bug': 'Bug / issue',
  'feedback.categoryError': 'Please select a category.',
  'feedback.ratingValidationMessage': '* Written feedback is required for low ratings.',

  // Common actions (backend translator aliases use this namespace)
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.submit': 'Submit',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',

  // Memory descriptions
  'settings.referenceSavedMemoriesDescription': 'Let Clara AI save and use memories when responding.',
  'settings.referenceChatHistoryDescription': 'Let Clara AI reference all previous conversations when responding.',

  // Common actions
  'btn.apply': 'Apply',
  'btn.cancel': 'Cancel',
  'btn.reset': 'Reset',
  'btn.save': 'Save',
  'btn.delete': 'Delete',
  'btn.close': 'Close',
  'btn.open': 'Open',
  'btn.next': 'Next',
  'btn.back': 'Back',
  'btn.viewAll': 'View all',

  // Focus / Morning Update — extended
  'focus.overnight.intro': "Here's what happened since you were last on Tuesday afternoon:",
  'focus.overnight.yourTeam': 'Your team',
  'focus.overnight.aiAgents': 'AI agents',
  'focus.overnight.aria.teamUpdates': 'Team updates',
  'focus.overnight.aria.aiAgentActivity': 'AI agent activity',

  // Dashboards — Workspace
  'dash.workspace.title': 'US Engagements',
  'dash.workspace.updated': 'Updated 3 min ago',
  'dash.kpi.activeEngagements': 'Active Engagements',
  'dash.kpi.openActions': 'Open Actions',
  'dash.kpi.inReview': 'In Review',
  'dash.kpi.flagged': 'Flagged',
  'dash.section.openWorkspaces': 'Open Workspaces',
  'dash.section.activityFeed': 'Activity Feed',
  'dash.aria.kpis': 'KPIs',
  'dash.aria.workspacesActivity': 'Workspaces and activity',

  // Dashboards — Clients
  'dash.clients.title': 'Clients',
  'dash.clients.statusDesc': 'On Track ↗ Improving',
  'dash.section.portfolioSummary': 'Portfolio Summary',
  'dash.section.reviewQueue': 'Review Queue',
  'dash.aria.engagementHealth': 'Engagement health',
  'dash.aria.portfolioDetails': 'Portfolio details',

  // Dashboards — Agents
  'dash.agents.title': 'All Agents Activity',
  'dash.agents.rows': 'rows',
  'dash.agents.col.agent': 'Agent',
  'dash.agents.col.project': 'Project',
  'dash.agents.col.workspace': 'Workspace',
  'dash.agents.col.name': 'Name',
  'dash.agents.col.lastUpdated': 'Last Updated',
  'dash.agents.col.status': 'Status',
  'agent.status.idle': 'Idle',
  'agent.status.running': 'Running',
  'agent.status.complete': 'Complete',
  'agent.status.failed': 'Failed',

  // Dashboards — Pulse
  'dash.pulse.title': 'Pulse Feed',
  'dash.pulse.unread': 'unread',
  'pulse.category.requiredAction': 'Required Action',
  'pulse.category.todayTask': "Today's Task",
  'pulse.category.clientInsight': 'Client Insight',
  'pulse.category.portfolioHealth': 'Portfolio Health',
  'pulse.category.latestNews': 'Latest News',

  // Clients dashboard — view toggle (Session 65 wiring)
  'view.table': 'Table',
  'view.charts': 'Charts',
  'view.toggle.aria': 'View mode',

  // BodyContent fallback
  'body.fallbackTitle': 'Page',
};
const pt: TranslationDict = { 
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portafolio',
  'nav.focus': 'Novo chat',
  'nav.workspace': 'Espaço de trabalho',
  'nav.home': 'Início',
  'nav.back': 'Voltar',
  'nav.agents': 'Agentes',
  'nav.clients': 'Clientes',
  'nav.workspaces': 'Espaços de trabalho',
  'nav.tabs': 'Abas',
  'nav.search.placeholder': 'Buscar',
  'dock.home': 'Início',
  'dock.clients': 'Clientes',
  'dock.workspaces': 'Espaços',
  'dock.agents': 'Agentes',
  'dock.pulse': 'Ver Pulse',
  'dock.newChat': 'Novo chat',
  'tab.chat': 'Chat',
  'tab.dashboard': 'Painel',
  'tab.newChat': 'Novo chat',
  'tab.newWorkspace': 'Novo espaço',
  'tab.newAgent': 'Novo agente',
  'tab.closeAllTabs': 'Fechar todas',
  'chat.placeholder': 'Pergunte à Clara...',
  'chat.askAnything': 'Pergunte o que quiser...',
  'chat.thinking': 'Pensando...',
  'chat.send': 'Enviar',
  'focus.greeting.morning': 'Bom dia',
  'focus.greeting.afternoon': 'Boa tarde',
  'focus.greeting.evening': 'Boa noite',
  'mode.work': 'Trabalho',
  'mode.web': 'Web',
  'eng.context': 'Context',
  'eng.reasoning': 'Reasoning',
  'eng.tasks': 'Tasks',
  'panel.context': 'Context',
  'panel.history': 'History',
  'panel.tasks': 'Tasks',
  'ai.claraAi': 'Clara AI',
  'profile.language': 'Language',
  'btn.apply': 'Apply',
  'btn.cancel': 'Cancelar',
  'btn.save': 'Guardar',
  'btn.close': 'Cerrar',
};
const es: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portafolio',
  'nav.focus': 'Nuevo chat',
  'nav.workspace': 'Espacio de trabajo',
  'nav.home': 'Inicio',
  'nav.back': 'Atrás',
  'nav.agents': 'Agentes',
  'nav.clients': 'Clientes',
  'nav.workspaces': 'Espacios de trabajo',
  'nav.tabs': 'Pestañas',
  'nav.search.placeholder': 'Buscar',
  'dock.home': 'Inicio',
  'dock.clients': 'Clientes',
  'dock.workspaces': 'Espacios',
  'dock.agents': 'Agentes',
  'dock.pulse': 'Ver Pulse',
  'tab.chat': 'Chat',
  'tab.dashboard': 'Panel',
  'tab.newChat': 'Nuevo chat',
  'tab.newWorkspace': 'Nuevo espacio',
  'tab.newAgent': 'Nuevo agente',
  'tab.closeAllTabs': 'Cerrar todas',
  'chat.placeholder': 'Pregúntale a Clara...',
  'chat.askAnything': 'Pregunta lo que sea...',
  'chat.thinking': 'Pensando...',
  'chat.send': 'Enviar',
  'focus.greeting.morning': 'Buenos días',
  'focus.greeting.afternoon': 'Buenas tardes',
  'focus.greeting.evening': 'Buenas noches',
  'focus.status.checking': 'Buscando actualizaciones ...',
  'mode.work': 'Trabajo',
  'mode.web': 'Web',
  'eng.context': 'Contexto',
  'eng.reasoning': 'Razonamiento',
  'eng.tasks': 'Tareas',
  'panel.context': 'Contexto',
  'panel.history': 'Historial',
  'ai.claraAi': 'Clara IA',
  'profile.language': 'Idioma',
  'btn.apply': 'Aplicar',
  'btn.cancel': 'Cancelar',
  'btn.save': 'Guardar',
  'btn.close': 'Cerrar',
};

const fr: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portefeuille',
  'nav.focus': 'Nouveau chat',
  'nav.workspace': 'Espace de travail',
  'nav.home': 'Accueil',
  'nav.back': 'Retour',
  'nav.agents': 'Agents',
  'nav.clients': 'Clients',
  'nav.workspaces': 'Espaces',
  'nav.tabs': 'Onglets',
  'nav.search.placeholder': 'Rechercher',
  'dock.home': 'Accueil',
  'dock.clients': 'Clients',
  'dock.workspaces': 'Espaces',
  'dock.agents': 'Agents',
  'dock.pulse': 'Voir Pulse',
  'tab.chat': 'Discussion',
  'tab.dashboard': 'Tableau',
  'tab.newChat': 'Nouvelle discussion',
  'tab.newWorkspace': 'Nouvel espace',
  'tab.newAgent': 'Nouvel agent',
  'tab.closeAllTabs': 'Fermer tout',
  'chat.placeholder': 'Demandez à Clara...',
  'chat.askAnything': 'Posez une question...',
  'chat.thinking': 'En réflexion...',
  'chat.send': 'Envoyer',
  'focus.greeting.morning': 'Bonjour',
  'focus.greeting.afternoon': 'Bon après-midi',
  'focus.greeting.evening': 'Bonsoir',
  'focus.status.checking': 'Vérification des mises à jour ...',
  'mode.work': 'Travail',
  'mode.web': 'Web',
  'eng.context': 'Contexte',
  'eng.reasoning': 'Raisonnement',
  'eng.tasks': 'Tâches',
  'panel.context': 'Contexte',
  'panel.history': 'Historique',
  'ai.claraAi': 'Clara IA',
  'profile.language': 'Langue',
  'btn.apply': 'Appliquer',
  'btn.cancel': 'Annuler',
  'btn.save': 'Enregistrer',
  'btn.close': 'Fermer',
};

const de: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portfolio',
  'nav.focus': 'Neuer Chat',
  'nav.workspace': 'Arbeitsbereich',
  'nav.home': 'Startseite',
  'nav.back': 'Zurück',
  'nav.agents': 'Agenten',
  'nav.clients': 'Kunden',
  'nav.workspaces': 'Arbeitsbereiche',
  'nav.tabs': 'Registerkarten',
  'nav.search.placeholder': 'Suchen',
  'dock.home': 'Start',
  'dock.clients': 'Kunden',
  'dock.workspaces': 'Bereiche',
  'dock.agents': 'Agenten',
  'dock.pulse': 'Pulse anzeigen',
  'tab.chat': 'Chat',
  'tab.dashboard': 'Dashboard',
  'tab.newChat': 'Neuer Chat',
  'tab.newWorkspace': 'Neuer Bereich',
  'tab.newAgent': 'Neuer Agent',
  'tab.closeAllTabs': 'Alle schließen',
  'chat.placeholder': 'Fragen Sie Clara...',
  'chat.askAnything': 'Fragen Sie etwas...',
  'chat.thinking': 'Denke nach...',
  'chat.send': 'Senden',
  'focus.greeting.morning': 'Guten Morgen',
  'focus.greeting.afternoon': 'Guten Tag',
  'focus.greeting.evening': 'Guten Abend',
  'focus.status.checking': 'Suche nach Updates ...',
  'mode.work': 'Arbeit',
  'mode.web': 'Web',
  'eng.context': 'Kontext',
  'eng.reasoning': 'Begründung',
  'eng.tasks': 'Aufgaben',
  'panel.context': 'Kontext',
  'panel.history': 'Verlauf',
  'ai.claraAi': 'Clara KI',
  'profile.language': 'Sprache',
  'btn.apply': 'Anwenden',
  'btn.cancel': 'Abbrechen',
  'btn.save': 'Speichern',
  'btn.close': 'Schließen',
};

const ja: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'ポートフォリオ',
  'nav.focus': '新規チャット',
  'nav.workspace': 'ワークスペース',
  'nav.home': 'ホーム',
  'nav.back': '戻る',
  'nav.agents': 'エージェント',
  'nav.clients': 'クライアント',
  'nav.workspaces': 'ワークスペース',
  'nav.tabs': 'タブ',
  'nav.search.placeholder': '検索',
  'dock.home': 'ホーム',
  'dock.clients': 'クライアント',
  'dock.workspaces': 'ワークスペース',
  'dock.agents': 'エージェント',
  'dock.pulse': 'Pulseを見る',
  'tab.chat': 'チャット',
  'tab.dashboard': 'ダッシュボード',
  'tab.newChat': '新規チャット',
  'tab.newWorkspace': '新規ワークスペース',
  'tab.newAgent': '新規エージェント',
  'tab.closeAllTabs': 'すべて閉じる',
  'chat.placeholder': 'Claraに質問...',
  'chat.askAnything': '何でも質問してください...',
  'chat.thinking': '思考中...',
  'chat.send': '送信',
  'focus.greeting.morning': 'おはようございます',
  'focus.greeting.afternoon': 'こんにちは',
  'focus.greeting.evening': 'こんばんは',
  'focus.status.checking': 'アップデートを確認中 ...',
  'mode.work': '仕事',
  'mode.web': 'ウェブ',
  'eng.context': 'コンテキスト',
  'eng.reasoning': '推論',
  'eng.tasks': 'タスク',
  'panel.context': 'コンテキスト',
  'panel.history': '履歴',
  'ai.claraAi': 'Clara AI',
  'profile.language': '言語',
  'btn.apply': '適用',
  'btn.cancel': 'キャンセル',
  'btn.save': '保存',
  'btn.close': '閉じる',
};
const zh: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': '投资组合',
  'nav.focus': '新建聊天',
  'nav.workspace': '工作区',
  'nav.home': '首页',
  'nav.back': '返回',
  'nav.agents': '代理',
  'nav.clients': '客户',
  'nav.workspaces': '工作区',
  'nav.tabs': '标签页',
  'nav.search.placeholder': '搜索',
  'dock.home': '首页',
  'dock.clients': '客户',
  'dock.workspaces': '工作区',
  'dock.agents': '代理',
  'dock.pulse': '查看 Pulse',
  'tab.chat': '聊天',
  'tab.dashboard': '仪表板',
  'tab.newChat': '新建聊天',
  'tab.newWorkspace': '新建工作区',
  'tab.newAgent': '新建代理',
  'tab.closeAllTabs': '全部关闭',
  'chat.placeholder': '向 Clara 提问...',
  'chat.askAnything': '随便问...',
  'chat.thinking': '思考中...',
  'chat.send': '发送',
  'focus.greeting.morning': '早上好',
  'focus.greeting.afternoon': '下午好',
  'focus.greeting.evening': '晚上好',
  'focus.status.checking': '检查更新中 ...',
  'mode.work': '工作',
  'mode.web': '网页',
  'eng.context': '上下文',
  'eng.reasoning': '推理',
  'eng.tasks': '任务',
  'panel.context': '上下文',
  'panel.history': '历史',
  'ai.claraAi': 'Clara AI',
  'profile.language': '语言',
  'btn.apply': '应用',
  'btn.cancel': '取消',
  'btn.save': '保存',
  'btn.close': '关闭',
};
const ko: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': '포트폴리오',
  'nav.focus': '새 채팅',
  'nav.workspace': '작업 공간',
  'nav.home': '홈',
  'nav.back': '뒤로',
  'nav.agents': '에이전트',
  'nav.clients': '클라이언트',
  'nav.workspaces': '작업 공간',
  'nav.tabs': '탭',
  'nav.search.placeholder': '검색',
  'dock.home': '홈',
  'dock.clients': '클라이언트',
  'dock.workspaces': '작업 공간',
  'dock.agents': '에이전트',
  'dock.pulse': 'Pulse 보기',
  'tab.chat': '채팅',
  'tab.dashboard': '대시보드',
  'tab.newChat': '새 채팅',
  'tab.newWorkspace': '새 작업 공간',
  'tab.newAgent': '새 에이전트',
  'tab.closeAllTabs': '모두 닫기',
  'chat.placeholder': 'Clara에게 질문...',
  'chat.askAnything': '무엇이든 물어보세요...',
  'chat.thinking': '생각 중...',
  'chat.send': '보내기',
  'focus.greeting.morning': '좋은 아침입니다',
  'focus.greeting.afternoon': '안녕하세요',
  'focus.greeting.evening': '안녕하세요',
  'focus.status.checking': '업데이트 확인 중 ...',
  'mode.work': '업무',
  'mode.web': '웹',
  'eng.context': '컨텍스트',
  'eng.reasoning': '추론',
  'eng.tasks': '작업',
  'panel.context': '컨텍스트',
  'panel.history': '히스토리',
  'ai.claraAi': 'Clara AI',
  'profile.language': '언어',
  'btn.apply': '적용',
  'btn.cancel': '취소',
  'btn.save': '저장',
  'btn.close': '닫기',

};
const it: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portafoglio',
  'nav.focus': 'Nuova chat',
  'nav.workspace': 'Spazio di lavoro',
  'nav.home': 'Home',
  'nav.back': 'Indietro',
  'nav.agents': 'Agenti',
  'nav.clients': 'Clienti',
  'nav.workspaces': 'Spazi di lavoro',
  'nav.tabs': 'Schede',
  'nav.search.placeholder': 'Cerca',
  'dock.home': 'Home',
  'dock.clients': 'Clienti',
  'dock.workspaces': 'Spazi di lavoro',
  'dock.agents': 'Agenti',
  'dock.pulse': 'Visualizza Pulse',
  'tab.chat': 'Chat',
  'tab.dashboard': 'Cruscotto',
  'tab.newChat': 'Nuova chat',
  'tab.newWorkspace': 'Nuovo spazio di lavoro',
  'tab.newAgent': 'Nuovo agente',
  'tab.closeAllTabs': 'Chiudi tutte le schede',
  'chat.placeholder': 'Chiedi a Clara...',
  'chat.askAnything': 'Chiedi qualsiasi cosa...',
  'chat.thinking': 'Sto pensando...',
  'chat.send': 'Invia',
  'focus.greeting.morning': 'Buongiorno',
  'focus.greeting.afternoon': 'Buon pomeriggio',
  'focus.greeting.evening': 'Buonasera',
  'focus.status.checking': 'Verifica aggiornamenti in corso ...',
  'mode.work': 'Lavoro',
  'mode.web': 'Web',
  'eng.context': 'Contesto',
  'eng.reasoning': 'Ragionamento',
  'eng.tasks': 'Attività',
  'panel.context': 'Contesto',
  'panel.history': 'Cronologia',
  'ai.claraAi': 'Clara AI',
  'profile.language': 'Lingua',
  'btn.apply': 'Applica',
  'btn.cancel': 'Annulla',
  'btn.save': 'Salva',
  'btn.close': 'Chiudi',
};
const nl: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portfolio',
  'nav.focus': 'Nieuwe chat',
  'nav.workspace': 'Werkruimte',
  'nav.home': 'Home',
  'nav.back': 'Terug',
  'nav.agents': 'Agenten',
  'nav.clients': 'Klanten',
  'nav.workspaces': 'Werkruimtes',
  'nav.tabs': 'Tabbladen',
  'nav.search.placeholder': 'Zoeken',
  'dock.home': 'Home',
  'dock.clients': 'Klanten',
  'dock.workspaces': 'Werkruimtes',
  'dock.agents': 'Agenten',
  'dock.pulse': 'Bekijk Pulse',
  'tab.chat': 'Chat',
  'tab.dashboard': 'Dashboard',
  'tab.newChat': 'Nieuwe chat',
  'tab.newWorkspace': 'Nieuwe werkruimte',
  'tab.newAgent': 'Nieuwe agent',
  'tab.closeAllTabs': 'Sluit alle tabbladen',
  'chat.placeholder': 'Vraag het aan Clara...',
  'chat.askAnything': 'Vraag wat dan ook...',
  'chat.thinking': 'Ik denk na...',
  'chat.send': 'Verzenden',
  'focus.greeting.morning': 'Goedemorgen',
  'focus.greeting.afternoon': 'Goedemiddag',
  'focus.greeting.evening': 'Goedenavond',
  'focus.status.checking': 'Updates controleren...',
  'mode.work': 'Werk',
  'mode.web': 'Web',
  'eng.context': 'Context',
  'eng.reasoning': 'Redenering',
  'eng.tasks': 'Taken',
  'panel.context': 'Context',
  'panel.history': 'Geschiedenis',
  'ai.claraAi': 'Clara AI',
  'profile.language': 'Taal',
  'btn.apply': 'Toepassen',
  'btn.cancel': 'Annuleren',
  'btn.save': 'Opslaan',
  'btn.close': 'Sluiten',
};
const ar: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'محفظة',
  'nav.focus': 'دردشة جديدة',
  'nav.workspace': 'مساحة العمل',
  'nav.home': 'الصفحة الرئيسية',
  'nav.back': 'رجوع',
  'nav.agents': 'وكلاء',
  'nav.clients': 'عملاء',
  'nav.workspaces': 'مساحات العمل',
  'nav.tabs': 'علامات التبويب',
  'nav.search.placeholder': 'بحث',
  'dock.home': 'الصفحة الرئيسية',
  'dock.clients': 'عملاء',
  'dock.workspaces': 'مساحات العمل',
  'dock.agents': 'وكلاء',
  'dock.pulse': 'عرض Pulse',
  'tab.chat': 'دردشة',
  'tab.dashboard': 'لوحة القيادة',
  'tab.newChat': 'دردشة جديدة',
  'tab.newWorkspace': 'مساحة عمل جديدة',
  'tab.newAgent': 'وكيل جديد',
  'tab.closeAllTabs': 'إغلاق جميع علامات التبويب',
  'chat.placeholder': 'اسأل كلارا...',
  'chat.askAnything': 'اسأل أي شيء...',
  'chat.thinking': 'أفكر...',
  'chat.send': 'إرسال',
  'focus.greeting.morning': 'صباح الخير',
  'focus.greeting.afternoon': 'مساء الخير',
  'focus.greeting.evening': 'مساء الخير',
  'focus.status.checking': 'جارٍ التحقق من التحديثات...',
  'mode.work': 'عمل',
  'mode.web': 'ويب',
  'eng.context': 'السياق',
  'eng.reasoning': 'الاستدلال',
  'eng.tasks': 'المهام',
  'panel.context': 'السياق',
  'panel.history': 'التاريخ',
  'ai.claraAi': 'كلارا AI',
  'profile.language': 'اللغة',
  'btn.apply': 'تطبيق',
  'btn.cancel': 'إلغاء',
  'btn.save': 'حفظ',
  'btn.close': 'إغلاق',
};
const hi: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'पोर्टफोलियो',
  'nav.focus': 'नई चैट',
  'nav.workspace': 'कार्यस्थान',
  'nav.home': 'होम',
  'nav.back': 'वापस',
  'nav.agents': 'एजेंट',
  'nav.clients': 'क्लाइंट',
  'nav.workspaces': 'कार्यस्थल',
  'nav.tabs': 'टैब',
  'nav.search.placeholder': 'खोजें',
  'dock.home': 'होम',
  'dock.clients': 'क्लाइंट',
  'dock.workspaces': 'कार्यस्थल',
  'dock.agents': 'एजेंट',
  'dock.pulse': 'पल्स देखें',
  'tab.chat': 'चैट',
  'tab.dashboard': 'डैशबोर्ड',
  'tab.newChat': 'नई चैट',
  'tab.newWorkspace': 'नया कार्यस्थान',
  'tab.newAgent': 'नया एजेंट',
  'tab.closeAllTabs': 'सभी टैब बंद करें',
  'chat.placeholder': 'क्लारा से पूछें...',
  'chat.askAnything': 'कुछ भी पूछें...',
  'chat.thinking': 'सोच रहा हूँ...',
  'chat.send': 'भेजें',
  'focus.greeting.morning': 'सुप्रभात',
  'focus.greeting.afternoon': 'नमस्कार',
  'focus.greeting.evening': 'शुभ संध्या',
  'focus.status.checking': 'अपडेट की जाँच हो रही है...',
  'mode.work': 'काम',
  'mode.web': 'वेब',
  'eng.context': 'संदर्भ',
  'eng.reasoning': 'तर्क',
  'eng.tasks': 'कार्य',
  'panel.context': 'संदर्भ',
  'panel.history': 'इतिहास',
  'ai.claraAi': 'क्लारा AI',
  'profile.language': 'भाषा',
  'btn.apply': 'लागू करें',
  'btn.cancel': 'रद्द करें',
  'btn.save': 'सहेजें',
  'btn.close': 'बंद करें',
};
const th: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'พอร์ตโฟลิโอ',
  'nav.focus': 'แชทใหม่',
  'nav.workspace': 'พื้นที่ทำงาน',
  'nav.home': 'หน้าแรก',
  'nav.back': 'ย้อนกลับ',
  'nav.agents': 'ตัวแทน',
  'nav.clients': 'ลูกค้า',
  'nav.workspaces': 'พื้นที่ทำงาน',
  'nav.tabs': 'แท็บ',
  'nav.search.placeholder': 'ค้นหา',
  'dock.home': 'หน้าแรก',
  'dock.clients': 'ลูกค้า',
  'dock.workspaces': 'พื้นที่ทำงาน',
  'dock.agents': 'ตัวแทน',
  'dock.pulse': 'ดู Pulse',
  'tab.chat': 'แชท',
  'tab.dashboard': 'แดชบอร์ด',
  'tab.newChat': 'แชทใหม่',
  'tab.newWorkspace': 'พื้นที่ทำงานใหม่',
  'tab.newAgent': 'ตัวแทนใหม่',
  'tab.closeAllTabs': 'ปิดแท็บทั้งหมด',
  'chat.placeholder': 'ถามคลารา...',
  'chat.askAnything': 'ถามอะไรก็ได้...',
  'chat.thinking': 'กำลังคิด...',
  'chat.send': 'ส่ง',
  'focus.greeting.morning': 'สวัสดีตอนเช้า',
  'focus.greeting.afternoon': 'สวัสดีตอนบ่าย',
  'focus.greeting.evening': 'สวัสดีตอนเย็น',
  'focus.status.checking': 'กำลังตรวจสอบการอัปเดต...',
  'mode.work': 'งาน',
  'mode.web': 'เว็บ',
  'eng.context': 'บริบท',
  'eng.reasoning': 'การให้เหตุผล',
  'eng.tasks': 'งาน',
  'panel.context': 'บริบท',
  'panel.history': 'ประวัติ',
  'ai.claraAi': 'คลารา AI',
  'profile.language': 'ภาษา',
  'btn.apply': 'ใช้',
  'btn.cancel': 'ยกเลิก',
  'btn.save': 'บันทึก',
  'btn.close': 'ปิด',
};
const pl: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portfolio',
  'nav.focus': 'Nuova chat',
  'nav.workspace': 'Spazio di lavoro',
  'nav.home': 'Home',
  'nav.back': 'Indietro',
  'nav.agents': 'Agenti',
  'nav.clients': 'Clienti',
  'nav.workspaces': 'Spazi di lavoro',
  'nav.tabs': 'Schede',
  'nav.search.placeholder': 'Cerca',
  'dock.home': 'Home',
  'dock.clients': 'Clienti',
  'dock.workspaces': 'Spazi di lavoro',
  'dock.agents': 'Agenti',
  'dock.pulse': 'Visualizza Pulse', 
  'tab.chat': 'Chat',
  'tab.dashboard': 'Dashboard',
  'tab.newChat': 'Nuova chat',
  'tab.newWorkspace': 'Nuovo spazio di lavoro',
  'tab.newAgent': 'Nuovo agente',
  'tab.closeAllTabs': 'Chiudi tutte le schede',
  'chat.placeholder': 'Chiedi a Clara...',
  'chat.askAnything': 'Chiedi qualsiasi cosa...',
  'chat.thinking': 'Sto pensando...',
  'chat.send': 'Invia',
  'focus.greeting.morning': 'Buongiorno',
  'focus.greeting.afternoon': 'Buon pomeriggio',
  'focus.greeting.evening': 'Buonasera',
  'focus.status.checking': 'Controllo aggiornamenti...',
  'mode.work': 'Lavoro',
  'mode.web': 'Web',
  'eng.context': 'Contesto',
  'eng.reasoning': 'Ragionamento',
  'eng.tasks': 'Compiti',
  'panel.context': 'Contesto',
  'panel.history': 'Cronologia',
  'ai.claraAi': 'Clara AI',
  'profile.language': 'Lingua',
  'btn.apply': 'Applica',
  'btn.cancel': 'Annulla',
  'btn.save': 'Salva',
  'btn.close': 'Chiudi',
};
const tr: TranslationDict = {
  'header.appname': 'Clara intelligence',
  'nav.portfolio': 'Portföy',
  'nav.focus': 'Yeni sohbet',
  'nav.workspace': 'Çalışma alanı',
  'nav.home': 'Ana sayfa',
  'nav.back': 'Geri',
  'nav.agents': 'Ajanlar',
  'nav.clients': 'Müşteriler',
  'nav.workspaces': 'Çalışma alanları',
  'nav.tabs': 'Sekmeler',
  'nav.search.placeholder': 'Ara',
  'dock.home': 'Ana sayfa',
  'dock.clients': 'Müşteriler',
  'dock.workspaces': 'Çalışma alanları',
  'dock.agents': 'Ajanlar',
  'dock.pulse': 'Pulse\'u Görüntüle',
  'tab.chat': 'Sohbet',
  'tab.dashboard': 'Gösterge Paneli',
  'tab.newChat': 'Yeni sohbet',
  'tab.newWorkspace': 'Yeni çalışma alanı',
  'tab.newAgent': 'Yeni ajan',
  'tab.closeAllTabs': 'Tüm sekmeleri kapat',
  'chat.placeholder': 'Clara\'ya sor...',
  'chat.askAnything': 'Her şeyi sor...',
  'chat.thinking': 'Düşünüyorum...',
  'chat.send': 'Gönder',
  'focus.greeting.morning': 'Günaydın',
  'focus.greeting.afternoon': 'Tünaydın',
  'focus.greeting.evening': 'İyi akşamlar',
  'focus.status.checking': 'Güncellemeler kontrol ediliyor...',
  'mode.work': 'İş',
  'mode.web': 'Web',
  'eng.context': 'Bağlam',
  'eng.reasoning': 'Muhakeme',
  'eng.tasks': 'Görevler',
  'panel.context': 'Bağlam',
  'panel.history': 'Geçmiş',
  'ai.claraAi': 'Clara AI',
  'profile.language': 'Dil',
  'btn.apply': 'Uygula',
  'btn.cancel': 'İptal',
  'btn.save': 'Kaydet',
  'btn.close': 'Kapat',
};
/**
 * The full translation table. New languages can be added as partial
 * objects — the fallback chain in `t()` covers missing keys.
 */
export const TRANSLATIONS: Record<SupportedLang, TranslationDict> = {
  en,
  es,
  fr,
  de,
  pt,
  ja,
  zh,
  ko,
  it,
  nl,
  ar,
  hi,
  th,
  pl,
  tr,
};